import { Suspense, lazy } from "react";
import { LazyMotion, domAnimation } from "framer-motion";

import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import { MatomoTracker } from "@/components/MatomoTracker";
import { UPDATES_SECTION } from "@/features/blog/sections";


// Critical pages - load immediately
import Index from "./pages/Index";
import { GlobalExitIntent } from "@/components/GlobalExitIntent";

// Lazy load non-critical pages
const Solutions = lazy(() => import("./pages/Solutions"));
const StartHere = lazy(() => import("./pages/StartHere"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Contact = lazy(() => import("./pages/Contact"));

// Lazy load non-critical pages

const Resources = lazy(() => import("./pages/Resources"));
// AICapabilities absorbed into /platform/ 2026-07-10. 301 in public/_redirects
const Platform = lazy(() => import("./pages/Platform"));
const MissedCallTextBack = lazy(() => import("./pages/platform/MissedCallTextBack"));
const SpeedToLead = lazy(() => import("./pages/platform/SpeedToLead"));
const AIReceptionist = lazy(() => import("./pages/platform/AIReceptionist"));
const Reputation = lazy(() => import("./pages/platform/Reputation"));
const DatabaseReactivation = lazy(() => import("./pages/platform/DatabaseReactivation"));
const BusinessPhoneSystem = lazy(() => import("./pages/platform/BusinessPhoneSystem"));
const PrivacyPolicy = lazy(() => import("@/features/legal/PrivacyPolicy"));
const Security = lazy(() => import("@/features/legal/Security"));
const Terms = lazy(() => import("@/features/legal/Terms"));
const UsageBilling = lazy(() => import("@/features/legal/UsageBilling"));
const AcceptableUse = lazy(() => import("@/features/legal/AcceptableUse"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Careers = lazy(() => import("./pages/Careers"));
const BookConsultation = lazy(() => import("./pages/BookConsultation"));
const DigitalAudit = lazy(() => import("./pages/DigitalAudit"));
const Blog = lazy(() => import("@/features/blog/Blog"));
const BlogPost = lazy(() => import("@/features/blog/BlogPost"));
const Updates = lazy(() => import("@/features/updates/Updates"));
const CrmGrowth = lazy(() => import("./pages/solutions/CrmGrowth"));
const LeadGen = lazy(() => import("./pages/solutions/LeadGen"));
const Websites = lazy(() => import("./pages/solutions/Websites"));
const LocalSeoReputation = lazy(() => import("./pages/solutions/LocalSeoReputation"));
// WebSeo split into Websites + LocalSeoReputation, CustomAI merged into AIStrategy (2026-07-10). 301s in public/_redirects
const AIStrategy = lazy(() => import("./pages/solutions/AIStrategy"));
const About = lazy(() => import("./pages/About"));
const WebsiteLaunchProgram = lazy(() => import("./pages/WebsiteLaunchProgram"));

const Sitemap = lazy(() => import("./pages/Sitemap"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const ServiceAreas = lazy(() => import("@/pages/ServiceAreas"));

const Toronto = lazy(() => import("./pages/service-areas/Toronto"));
const Sudbury = lazy(() => import("./pages/service-areas/Sudbury"));


const ThunderBay = lazy(() => import("./pages/service-areas/ThunderBay"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Automotive = lazy(() => import("./pages/Automotive"));

const AutomotiveDealers = lazy(() => import("./pages/automotive/Dealers"));
const AutomotiveGtaDealers = lazy(() => import("./pages/automotive/GtaDealers"));
const Trades = lazy(() => import("./pages/Trades"));
const CaseStudies = lazy(() => import("./pages/CaseStudies"));
const NeedACar = lazy(() => import("./pages/case-studies/NeedACar"));
const JCTraining = lazy(() => import("./pages/case-studies/JCTraining"));
const GlobalAluminum = lazy(() => import("./pages/case-studies/GlobalAluminum"));
// Six thin case studies retired 2026-07-10 (hub carries the six real ones). 301s in public/_redirects
const SouthCoreRoar = lazy(() => import("./pages/case-studies/SouthCoreRoar"));
const CentennialFamilyFoods = lazy(() => import("./pages/case-studies/CentennialFamilyFoods"));
const MotoringDistrict = lazy(() => import("./pages/case-studies/MotoringDistrict"));

const ScaleSystem = lazy(() => import("./pages/solutions/ScaleSystem"));
const CanadaAIStrategy = lazy(() => import("./pages/CanadaAIStrategy"));
const Partners = lazy(() => import("./pages/Partners"));
const EnterpriseConfirmation = lazy(() => import("./pages/EnterpriseConfirmation"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LazyMotion features={domAnimation}>
      <TooltipProvider>
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <MatomoTracker />
          <Suspense fallback={
            <div className="min-h-screen flex items-center justify-center">
              <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
            </div>
          }>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/canada-ai-strategy-small-business" element={<CanadaAIStrategy />} />
              <Route path="/partners" element={<Partners />} />
              <Route path="/about" element={<About />} />
              <Route path="/solutions" element={<Solutions />} />
              <Route path="/solutions/crm-automation" element={<CrmGrowth />} />
              <Route path="/solutions/lead-generation-system" element={<LeadGen />} />
              <Route path="/solutions/websites" element={<Websites />} />
              <Route path="/solutions/local-seo-reputation" element={<LocalSeoReputation />} />
              <Route path="/solutions/ai-strategy-rollout" element={<AIStrategy />} />
              <Route path="/start" element={<StartHere />} />
              <Route path="/pricing" element={<Pricing />} />
              {/* /pricing/startup/ absorbed into the Launch Program 2026-07-10. 301 in public/_redirects */}
              <Route path="/website-launch-program" element={<WebsiteLaunchProgram />} />

              <Route path="/resources" element={<Resources />} />
              <Route path="/platform" element={<Platform />} />
              <Route path="/platform/missed-call-text-back" element={<MissedCallTextBack />} />
              <Route path="/platform/speed-to-lead" element={<SpeedToLead />} />
              <Route path="/platform/ai-receptionist" element={<AIReceptionist />} />
              <Route path="/platform/reputation-management" element={<Reputation />} />
              <Route path="/platform/database-reactivation" element={<DatabaseReactivation />} />
              <Route path="/platform/business-phone-system" element={<BusinessPhoneSystem />} />
              <Route path="/case-studies" element={<CaseStudies />} />
              <Route path="/case-studies/need-a-car-automotive-growth" element={<NeedACar />} />
              <Route path="/case-studies/jc-training-professional-services" element={<JCTraining />} />
              <Route path="/case-studies/global-aluminum-lead-generation" element={<GlobalAluminum />} />
              <Route path="/case-studies/south-core-roar-event-platform" element={<SouthCoreRoar />} />
              <Route path="/case-studies/centennial-family-foods-retail-system" element={<CentennialFamilyFoods />} />
              <Route path="/case-studies/the-motoring-district-inventory-engine" element={<MotoringDistrict />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/category/:category" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/updates" element={<Updates />} />
              <Route path="/updates/:slug" element={<BlogPost section={UPDATES_SECTION} />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/security" element={<Security />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/usage-billing" element={<UsageBilling />} />
              <Route path="/acceptable-use" element={<AcceptableUse />} />

              <Route path="/solutions/ad-management-scale" element={<ScaleSystem />} />
              <Route path="/enterprise-confirmation" element={<EnterpriseConfirmation />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/book-consultation" element={<BookConsultation />} />
              <Route path="/digital-audit" element={<DigitalAudit />} />
              <Route path="/onboarding" element={<Onboarding />} />


              <Route path="/automotive" element={<Automotive />} />
              <Route path="/automotive/dealers" element={<AutomotiveDealers />} />
              <Route path="/automotive/gta-dealers" element={<AutomotiveGtaDealers />} />
              <Route path="/industries/trades" element={<Trades />} />
              {/* Trades sub-pages, service-businesses & home-services consolidated 2026-06-13
                  (zero search traction / off Auto+Trades focus). 301'd in public/_redirects */}
              <Route path="/sitemap" element={<Sitemap />} />
              <Route path="/service-areas" element={<ServiceAreas />} />
              <Route path="/service-areas/toronto" element={<Toronto />} />
              <Route path="/service-areas/sudbury" element={<Sudbury />} />


              <Route path="/service-areas/thunder-bay" element={<ThunderBay />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            <GlobalExitIntent />
          </Suspense>

        </BrowserRouter>
      </TooltipProvider>
    </LazyMotion>
  </QueryClientProvider >
);

export default App;
